import React, { memo, useCallback, useMemo } from "react";
import clsx from "clsx";
import { FormGroup, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";

import { Button } from "components";
import { uiActions } from "store";

import { HookFormInput, InputProps } from "./hookFormInput";

import { ReactComponent as CopyIcon } from "static/images/copy_icon.svg";
import styles from "./fields.module.scss";

const defaultProps = {
	emptyLabel: false,
	colLabel: 6,
	colInput: 6,
};

type InputWithCopy = {
	isCopyButton?: boolean;
	getValues?: Function;
};
type InputWithButton = {
	button?: React.ReactNode;
};

type InputWithIcon = {
	iconName?: string;
};

type InputExternalContent = {
	external?: React.ReactNode;
};

export type HookFormInputGroupProps = InputProps &
	InputWithIcon &
	InputExternalContent &
	InputWithCopy &
	InputWithButton & {
		isPaddingRight?: boolean;
		showRequiredMark?: boolean;
		labelClassName?: string;
		inputBlockClassName?: string;
		hidden?: boolean;
		controlId?: string;
		solveCaretIssue?: Function;
		addon?: string | React.ReactNode;
	};

const HookFormInputGroup = ({
	name,
	register,
	defaultValue,
	label,
	labelHtml,
	errors,
	emptyLabel,
	type,
	placeholder,
	colLabel = defaultProps.colLabel,
	colInput = defaultProps.colInput,
	rows,
	normalizeOnBlur,
	normalize,
	validator,
	disabled,
	isSelectionDisabled,
	loader,
	loading,
	blurLoading,
	onChange,
	onBlur,
	onFocus,
	className,
	errorClassName,
	as,
	showRequiredMark,
	labelClassName,
	valueClassName,
	inputBlockClassName,
	warningText,
	showWarningOnFocus,
	warningClassName,
	readOnly,
	isCopyButton,
	hidden = false,
	isTooltip,
	tooltipTitle,
	autoComplete,
	controlId,
	isPlainText,
	placeholderPosition,
	canShowErrorIcon,
	solveCaretIssue,
	iconName,
	// can be used for render content outside under the input
	external,
	addon,
	button,
	maxLength,
}: HookFormInputGroupProps) => {
	const formMethods = useFormContext();
	const dispatch = useDispatch();

	const inputValue = useMemo(() => {
		if (!formMethods?.getValues) return undefined;
		return formMethods.getValues(name);
	}, [formMethods, name]);

	const isCopyButtonDisabled = useMemo(() => {
		return !inputValue || inputValue.length === 0;
	}, [inputValue]);

	const handleCopyClick = useCallback(() => {
		if (isCopyButtonDisabled) return;

		navigator.clipboard.writeText(inputValue).then(() =>
			dispatch(
				uiActions.showInfoNotification({
					title: `${label} has been copied`,
				}),
			),
		);
	}, [isCopyButtonDisabled, inputValue, dispatch, label]);

	const isHiddenClass = type === "hidden" || hidden ? styles.isHidden : "";

	return (
		<FormGroup
			as={Row}
			className={clsx(className, styles.formGroup, isHiddenClass)}
			controlId={controlId || name}
		>
			<HookFormInput
				name={name}
				register={register}
				defaultValue={defaultValue}
				label={label}
				labelHtml={labelHtml}
				errors={errors}
				emptyLabel={emptyLabel}
				type={type}
				placeholder={placeholder}
				colLabel={colLabel}
				colInput={colInput}
				rows={rows}
				normalizeOnBlur={normalizeOnBlur}
				normalize={normalize}
				validator={validator}
				disabled={disabled}
				isSelectionDisabled={isSelectionDisabled}
				loader={loader}
				loading={loading}
				blurLoading={blurLoading}
				onChange={onChange}
				onBlur={onBlur}
				onFocus={onFocus}
				labelClassName={labelClassName}
				valueClassName={valueClassName}
				inputBlockClassName={inputBlockClassName}
				showRequiredMark={showRequiredMark}
				as={as}
				warningText={warningText}
				showWarningOnFocus={showWarningOnFocus}
				readOnly={readOnly}
				errorClassName={errorClassName}
				isTooltip={isTooltip}
				tooltipTitle={tooltipTitle}
				autoComplete={autoComplete}
				warningClassName={warningClassName}
				isPlainText={isPlainText}
				placeholderPosition={placeholderPosition}
				canShowErrorIcon={canShowErrorIcon}
				solveCaretIssue={solveCaretIssue}
				external={external}
				addon={addon}
				maxLength={maxLength}
			/>

			{iconName && !blurLoading && (
				<div className={styles.iconHolder}>
					<svg>
						<use href={`#${iconName}_sprite_icon`}></use>
					</svg>
				</div>
			)}
			{isCopyButton && (
				<Button
					btnIcon
					onClick={handleCopyClick}
					icon={<CopyIcon />}
					title="Copy"
					variant="secondary"
					size="sm"
					className={styles.copyBtn}
					disabled={isCopyButtonDisabled}
				/>
			)}
			{button && !blurLoading && (
				<div className={styles.buttonHolder}>{button}</div>
			)}
		</FormGroup>
	);
};

const component = memo(HookFormInputGroup);
export { component as HookFormInputGroup };
